import LoaderOverlay from "@edaz/edazkit/LoaderOverlay";
import type { ViewComponent } from "@frui.ts/views/dist";
import { registerView } from "@frui.ts/views/dist";
import type VehicleRegisterVehicleChangeResponse from "entities/vehicleRegisterVehicleChangeResponse";
import type VehicleRegisterVehicleClassifiedFuelType from "entities/vehicleRegisterVehicleClassifiedFuelType";
import VehicleRegisterVehiclePropertyChangeDisplayType from "entities/vehicleRegisterVehiclePropertyChangeDisplayType";
import type VehicleRegisterVehicleStatus from "entities/vehicleRegisterVehicleStatus";
import { observer } from "mobx-react-lite";
import React from "react";
import { emptyValue } from "utils/emptyValue";
import { formatDate, formatDateTime } from "utils/localizations";
import VehicleRegisterHistoriesViewModel from "viewModels/callCenter/search/vehicleRegister/vehicleRegisterDetail/vehicleRegisterHistoriesViewModel";

// eslint-disable-next-line sonarjs/cognitive-complexity
const VehicleRegisterHistoriesView: ViewComponent<VehicleRegisterHistoriesViewModel> = observer(({ vm }) => {
  if (!vm.vehicleRegister) {
    return null;
  }

  function changeValueFormatter(item: VehicleRegisterVehicleChangeResponse, type: "new" | "original") {
    const value = type === "new" ? item.newValue : item.originalValue;
    const translateValue = vm.localizationService.translateEnum(
      "vehicleRegisterVehiclePropertyChangeDisplayType",
      value as VehicleRegisterVehiclePropertyChangeDisplayType
    );
    if (!value) {
      return emptyValue;
    }
    if (!translateValue.includes(".")) {
      return translateValue;
    }

    switch (item.changeType) {
      case VehicleRegisterVehiclePropertyChangeDisplayType.ClassifiedFuelTypeChange:
        return vm.translateVehicleRegisterVehicleClassifiedFuelType(value as VehicleRegisterVehicleClassifiedFuelType);

      case VehicleRegisterVehiclePropertyChangeDisplayType.VehicleStatusChange:
        return vm.translateVehicleStatus(value as VehicleRegisterVehicleStatus);

      case VehicleRegisterVehiclePropertyChangeDisplayType.MaximumAllowedWeightChange:
        return vm.formatHeight(Number(value));

      case VehicleRegisterVehiclePropertyChangeDisplayType.FuelTypesChange: {
        return vm.translateVehicleRegisterVehicleFuelType(value);
      }

      case VehicleRegisterVehiclePropertyChangeDisplayType.LicensePlateValidSinceDateChange:
      case VehicleRegisterVehiclePropertyChangeDisplayType.LicensePlateValidUntilDateChange:
      case VehicleRegisterVehiclePropertyChangeDisplayType.VehicleStatusValidSinceDateChange:
      case VehicleRegisterVehiclePropertyChangeDisplayType.VehicleStatusValidUntilDateChange:
        return <span className="text-nowrap">{formatDate(new Date(value))}</span>;

      default:
        return value;
    }
  }

  return (
    <>
      <LoaderOverlay loading={vm.busyWatcher.isBusy}>
        <table className="table table-full table-tbody-striped tbody-larger">
          <thead>
            <tr>
              <th>{vm.translateAttribute("changedAt")}</th>
              <th>{vm.translateChangeAttribute("changeType")}</th>
              <th>{vm.translateChangeAttribute("originalValue")}</th>
              <th style={{ width: 70 }}></th>
              <th>{vm.translateChangeAttribute("newValue")}</th>
              <th>{vm.translateAttribute("changedBy")}</th>
              <th>{vm.translateAttribute("changeOrigin")}</th>
            </tr>
          </thead>
          <>
            {vm.transformedItems.map((item, indexItem) => {
              return (
                <>
                  <tbody key={`${item.changedAt}-${item.changeOrigin}`}>
                    <tr key={`${item.changedAt}-${item.changeType}`} className={`${(indexItem + 1) % 2 === 0 ? "even" : "odd"}`}>
                      <td style={{ width: "150px" }}>{formatDateTime(item.changedAt)}</td>
                      <td>
                        <strong className="text-success">{vm.translateChangeType(item.changeType)}</strong>
                      </td>
                      <td>{item.originalValue ? changeValueFormatter(item, "original") : emptyValue}</td>
                      <td>→</td>
                      <td>{item.newValue ? changeValueFormatter(item, "new") : emptyValue}</td>
                      <td>{item.changedBy ? item.changedBy : vm.translate("system")}</td>
                      <td>{vm.translateChangeOrigin(item.changeOrigin)}</td>
                    </tr>
                  </tbody>
                </>
              );
            })}
          </>
        </table>
        {vm.items?.length === 0 && (
          <div className="bg-white mt-n2 mb-4 px-4 py-3 border-bottom text-muted full-width">
            {vm.translateGeneral(vm.busyWatcher.isBusy ? "loading" : "empty")}
          </div>
        )}
      </LoaderOverlay>
    </>
  );
});

registerView(VehicleRegisterHistoriesView, VehicleRegisterHistoriesViewModel);
